import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { CtaBlock } from "../../components/CtaBlock";
import { RelatedArticles } from "../../components/RelatedArticles";

const ARTICLE = {
	title: "メルカリ 商品写真 最適サイズ 1200×900 余白なしで売れる画像の作り方",
	description:
		"メルカリで売れる商品写真のサイズ・余白なし・白背景の作り方を解説。1080×1080px推奨の正方形で映える写真を撮るコツも紹介。",
	publishDate: "2026-03-16",
	slug: "mercari-product-image-size-1200x900",
};

const ogImageUrl = `https://og.fucina.casa/api/og?title=${encodeURIComponent(ARTICLE.title)}&description=${encodeURIComponent(ARTICLE.description)}&locale=ja`;

export default function MercariProductImageSize1200x900() {
	return (
		<>
			<Helmet>
				<title>{ARTICLE.title} | しろぬきや</title>
				<meta name="description" content={ARTICLE.description} />
				<meta property="og:title" content={ARTICLE.title} />
				<meta property="og:description" content={ARTICLE.description} />
				<meta
					property="og:url"
					content={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<meta property="og:type" content="article" />
				<meta property="og:image" content={ogImageUrl} />
				<meta name="twitter:card" content="summary_large_image" />
				<meta name="twitter:title" content={ARTICLE.title} />
				<meta name="twitter:description" content={ARTICLE.description} />
				<meta name="twitter:image" content={ogImageUrl} />
				<link
					rel="canonical"
					href={`https://shironukiya.fucina.casa/blog/${ARTICLE.slug}`}
				/>
				<script type="application/ld+json">
					{JSON.stringify({
						"@context": "https://schema.org",
						"@type": "Article",
						headline: ARTICLE.title,
						description: ARTICLE.description,
						datePublished: ARTICLE.publishDate,
						publisher: {
							"@type": "Organization",
							name: "しろぬきや",
							url: "https://shironukiya.fucina.casa",
						},
					})}
				</script>
			</Helmet>

			<div className="max-w-3xl mx-auto px-4 py-10">
				<nav className="text-sm text-gray-500 mb-6">
					<Link to="/" className="hover:underline">
						ホーム
					</Link>
					{" > "}
					<Link to="/blog" className="hover:underline">
						ブログ
					</Link>
					{" > "}
					<span>{ARTICLE.title}</span>
				</nav>

				<article>
					<h1 className="text-2xl font-bold mb-4">{ARTICLE.title}</h1>
					<p className="text-gray-500 text-sm mb-8">
						更新日: {ARTICLE.publishDate}
					</p>

					<p className="text-gray-700 mb-6">
						メルカリで商品を出品する際、「写真のサイズはどのくらいがいいの？」「余白があると売れないって本当？」と気になっていませんか？
						実は、メルカリの商品写真は<strong>サイズ・余白・背景</strong>
						を最適化するだけで売れやすさが大きく変わります。
						正しい設定方法をわかりやすく解説します。
					</p>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							メルカリ商品写真の基本要件
						</h2>
						<div className="overflow-x-auto mb-4">
							<table className="w-full text-sm border-collapse border border-gray-200">
								<thead>
									<tr className="bg-gray-50">
										<th className="border border-gray-200 px-4 py-2 text-left">
											項目
										</th>
										<th className="border border-gray-200 px-4 py-2 text-left">
											要件
										</th>
									</tr>
								</thead>
								<tbody>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											推奨サイズ
										</td>
										<td className="border border-gray-200 px-4 py-2">
											<strong>1080×1080px（正方形）</strong>
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">
											アスペクト比
										</td>
										<td className="border border-gray-200 px-4 py-2">
											<strong>1:1（正方形）が最適</strong>
										</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-4 py-2">
											ファイル形式
										</td>
										<td className="border border-gray-200 px-4 py-2">
											JPEG（.jpg）、PNG（.png）
										</td>
									</tr>
									<tr className="bg-gray-50">
										<td className="border border-gray-200 px-4 py-2">
											最大登録枚数
										</td>
										<td className="border border-gray-200 px-4 py-2">20枚</td>
									</tr>
									<tr>
										<td className="border border-gray-200 px-4 py-2">背景色</td>
										<td className="border border-gray-200 px-4 py-2">
											白背景推奨（透明はNG）
										</td>
									</tr>
								</tbody>
							</table>
						</div>
						<div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
							<p className="text-sm text-blue-800">
								<strong>💡 1200×900pxについて</strong>：「メルカリ
								1200×900」と検索する方が多いですが、これはヤフオクなど他フリマサービスの推奨サイズです。メルカリでは
								<strong>正方形（1080×1080px）</strong>
								が最適です。1200×900pxも出品できますが、表示時にトリミングされる場合があります。
							</p>
						</div>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							余白なしで売れる画像の作り方
						</h2>
						<p className="text-gray-700 mb-4">
							メルカリの一覧画面では、商品画像が正方形でサムネイル表示されます。
							余白が多い画像は
							<strong>商品が小さく見えて購入意欲が下がる</strong>
							傾向があります。 「商品を画像いっぱいに大きく見せる」が鉄則です。
						</p>
						<ol className="list-decimal list-inside space-y-4 text-gray-700">
							<li>
								<strong>
									スマートフォンのカメラを「スクエア（1:1）」モードに設定する
								</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									iPhoneの場合：カメラアプリ →
									「正方形」モードを選択。これだけで余白を最小化できます。
								</span>
							</li>
							<li>
								<strong>商品を画面いっぱいに配置して撮影する</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									商品の周囲に5〜10%程度のマージンを残す程度で、できるだけ大きく撮影します。
								</span>
							</li>
							<li>
								<strong>背景を白にする</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									白い紙や布の上に置いて撮影するか、AI背景除去ツールで後から白背景にします。
									透明（PNG透明背景）でアップロードすると黒く表示されるので注意。
								</span>
							</li>
							<li>
								<strong>1080×1080pxにリサイズする</strong>
								<br />
								<span className="text-sm ml-5 block mt-1">
									スマートフォンで撮影した写真は自動でサイズが調整されますが、PCから投稿する場合は1080×1080pxに揃えておくと安心です。
								</span>
							</li>
						</ol>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							1枚目の写真が特に重要な理由
						</h2>
						<p className="text-gray-700 mb-4">
							メルカリの検索結果・タイムラインには
							<strong>1枚目の写真のみ</strong>が表示されます。
							購入者は1枚目の写真だけを見て商品詳細を開くかどうか判断するため、
							最も見栄えのよい写真を1枚目にする必要があります。
						</p>
						<ul className="list-disc list-inside space-y-2 text-gray-700 text-sm mb-4">
							<li>商品全体が収まっている</li>
							<li>明るく清潔感がある（白背景が効果的）</li>
							<li>余白が少なく商品が大きく見える</li>
							<li>ピントが合っている</li>
						</ul>
					</section>

					<section>
						<h2 className="text-xl font-semibold mt-8 mb-4">
							しろぬきやで解決する方法
						</h2>
						<p className="text-gray-700 mb-4">
							背景の除去・白背景への変換・1080×1080pxへのリサイズを手作業で行うのは時間がかかります。
							<strong>しろぬきや</strong>
							を使えば、AI背景除去＋メルカリ向けの正方形リサイズがブラウザで完結します。
							画像はサーバーに送信しないのでプライバシーも安全。無料で1日5枚まで利用できます。
						</p>
						<CtaBlock />
					</section>

					<RelatedArticles current={ARTICLE.slug} />
				</article>
			</div>
		</>
	);
}
